import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Copy, Download } from "lucide-react";
import { toast } from "sonner";

export type SeoFaqItem = { question: string; answer: string };

export type SeoResult = {
  seo_title: string;
  seo_description: string;
  keywords: string[];
  tags: string[];
  seo_score: number;
  // New richer fields (optional for backwards-compat with old history rows)
  long_description?: string;
  slug?: string;
  bullets?: string[];
  image_alt_text?: string;
  faq?: SeoFaqItem[];
  score_breakdown?: string[];
};

export function SeoResultCard({ result, productName }: { result: SeoResult; productName?: string }) {
  const copy = (label: string, val: string) => {
    navigator.clipboard.writeText(val);
    toast.success(`${label} copied`);
  };

  const buildFullText = () => {
    const parts: string[] = [];
    if (productName) parts.push(`Product: ${productName}`);
    parts.push(`SEO Title:\n${result.seo_title}`);
    parts.push(`Meta Description:\n${result.seo_description}`);
    if (result.slug) parts.push(`URL Slug:\n${result.slug}`);
    if (result.bullets?.length) parts.push(`Bullet Points:\n- ${result.bullets.join("\n- ")}`);
    if (result.long_description) parts.push(`Long Description:\n${result.long_description}`);
    parts.push(`Keywords:\n${result.keywords.join(", ")}`);
    parts.push(`Tags:\n${result.tags.join(", ")}`);
    if (result.image_alt_text) parts.push(`Image Alt Text:\n${result.image_alt_text}`);
    if (result.faq?.length) {
      parts.push(
        `FAQ:\n${result.faq.map((f) => `Q: ${f.question}\nA: ${f.answer}`).join("\n\n")}`,
      );
    }
    parts.push(`SEO Score: ${result.seo_score}/100`);
    if (result.score_breakdown?.length) parts.push(`Score Breakdown:\n- ${result.score_breakdown.join("\n- ")}`);
    return parts.join("\n\n");
  };

  const copyAll = () => {
    navigator.clipboard.writeText(buildFullText());
    toast.success("All content copied");
  };

  const download = () => {
    const blob = new Blob([buildFullText()], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(productName ?? "seo").replace(/\s+/g, "-").toLowerCase()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const scoreColor =
    result.seo_score >= 80 ? "text-success" : result.seo_score >= 60 ? "text-warning" : "text-destructive";

  return (
    <Card className="bg-gradient-card border-border/50 shadow-elegant">
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle className="text-base text-muted-foreground">SEO Result</CardTitle>
          {productName && <p className="text-xl font-bold mt-1">{productName}</p>}
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={copyAll}><Copy className="h-3.5 w-3.5 mr-1.5" />Copy all</Button>
          <Button size="sm" variant="outline" onClick={download}><Download className="h-3.5 w-3.5 mr-1.5" />Export</Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">SEO Score</span>
            <span className={`text-2xl font-bold ${scoreColor}`}>{result.seo_score}<span className="text-sm text-muted-foreground">/100</span></span>
          </div>
          <Progress value={result.seo_score} className="h-2" />
          {result.score_breakdown?.length ? (
            <ul className="mt-3 space-y-1 text-xs text-muted-foreground list-disc list-inside">
              {result.score_breakdown.map((b, i) => (
                <li key={i}>{b}</li>
              ))}
            </ul>
          ) : null}
        </div>

        <Field label="SEO Title" onCopy={() => copy("Title", result.seo_title)}>
          <p className="text-base font-semibold">{result.seo_title}</p>
          <p className="text-xs text-muted-foreground mt-1">{result.seo_title.length} characters</p>
        </Field>

        <Field label="Meta Description" onCopy={() => copy("Description", result.seo_description)}>
          <p className="text-sm leading-relaxed">{result.seo_description}</p>
          <p className="text-xs text-muted-foreground mt-1">{result.seo_description.length} characters</p>
        </Field>

        {result.slug && (
          <Field label="URL Slug" onCopy={() => copy("Slug", result.slug!)}>
            <p className="text-sm font-mono text-muted-foreground break-all">/{result.slug}</p>
          </Field>
        )}

        {result.bullets?.length ? (
          <Field label="Bullet Points" onCopy={() => copy("Bullets", result.bullets!.map((b) => `• ${b}`).join("\n"))}>
            <ul className="space-y-1.5 text-sm">
              {result.bullets.map((b, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-primary mt-0.5">•</span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </Field>
        ) : null}

        {result.long_description && (
          <Field label="Long Description" onCopy={() => copy("Long description", result.long_description!)}>
            <p className="text-sm leading-relaxed whitespace-pre-wrap">{result.long_description}</p>
          </Field>
        )}

        <Field label="Keywords" onCopy={() => copy("Keywords", result.keywords.join(", "))}>
          <div className="flex flex-wrap gap-1.5">
            {result.keywords.map((k, i) => (
              <Badge key={i} variant="secondary" className="font-normal">{k}</Badge>
            ))}
          </div>
        </Field>

        <Field label="Tags" onCopy={() => copy("Tags", result.tags.join(", "))}>
          <div className="flex flex-wrap gap-1.5">
            {result.tags.map((t, i) => (
              <Badge key={i} className="bg-gradient-primary text-primary-foreground font-normal border-0">{t}</Badge>
            ))}
          </div>
        </Field>

        {result.image_alt_text && (
          <Field label="Image Alt Text" onCopy={() => copy("Alt text", result.image_alt_text!)}>
            <p className="text-sm leading-relaxed">{result.image_alt_text}</p>
          </Field>
        )}

        {result.faq?.length ? (
          <Field
            label="FAQ"
            onCopy={() =>
              copy(
                "FAQ",
                result.faq!.map((f) => `Q: ${f.question}\nA: ${f.answer}`).join("\n\n"),
              )
            }
          >
            <div className="space-y-3">
              {result.faq.map((f, i) => (
                <div key={i} className="rounded-md border border-border/50 bg-background/40 p-3">
                  <p className="text-sm font-semibold">{f.question}</p>
                  <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{f.answer}</p>
                </div>
              ))}
            </div>
          </Field>
        ) : null}
      </CardContent>
    </Card>
  );
}

function Field({ label, children, onCopy }: { label: string; children: React.ReactNode; onCopy: () => void }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
        <Button size="sm" variant="ghost" className="h-7 px-2" onClick={onCopy}><Copy className="h-3 w-3" /></Button>
      </div>
      {children}
    </div>
  );
}
